import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const Form_10 = () => {
    const navigate = useNavigate();

    // Define common fields
    const commonFields = [
        { label: 'Date', name: 'date', readOnly: true },
        { label: 'Line', name: 'line', type: 'select' },
        { label: 'Shift', name: 'shift', readOnly: true },
    ];

    // Define module-specific fields
    const moduleFields = [
        { label: 'Module ID', name: 'moduleId' },      
        { label: 'VOC', name: 'voc', type: 'number' },
        { label: 'ISC', name: 'isc', type: 'number' },
        { label: 'VMP', name: 'vmp', type: 'number' },
        { label: 'IMP', name: 'imp', type: 'number' },
        { label: 'PMAX', name: 'pmax', type: 'number' },
        { label: 'FF', name: 'ff', type: 'number' },
        { label: 'Module Efficiency', name: 'moduleEfficiency', type: 'number' },
        { label: 'Rated Current', name: 'ratedCurrent', type: 'number' },
        { label: 'Test Date', name: 'testDate', type: 'date' },
        { label: 'Test Time', name: 'testTime', type: 'time' },
    ];

    // State management
    const [lines, setLines] = useState([]);
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Get current date in YYYY-MM-DD format for max attribute
    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    };

    // Fetch lines from metadata and initialize form data
    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const fetchedLines = parsedMetadata.lines.map(item => item.Lines);
                setLines(fetchedLines);

                const savedData = sessionStorage.getItem('FlashTestWitnessF10formData');
                let parsedData = savedData ? JSON.parse(savedData) : null;

                const inspectionDate = sessionStorage.getItem('inspectionDate') || '';
                const inspectionShift = sessionStorage.getItem('inspectionShift') || '';

                const defaultModules = Array(5).fill(null).map(() => ({
                    moduleId: '',
                    voc: '',
                    isc: '',
                    vmp: '',
                    imp: '',
                    pmax: '',
                    ff: '',
                    moduleEfficiency: '',
                    ratedCurrent: '',
                    testDate: '',
                    testTime: '',
                }));

                const initialFormData = {
                    date: inspectionDate,
                    line: fetchedLines[0] || '',
                    shift: inspectionShift,
                };

                fetchedLines.forEach(line => {
                    initialFormData[line] = { modules: [...defaultModules] };
                });

                if (!parsedData) {
                    setFormData(initialFormData);
                } else {
                    setFormData({
                        date: parsedData.date || inspectionDate,
                        line: parsedData.line || fetchedLines[0] || '',
                        shift: parsedData.shift || inspectionShift,
                        ...fetchedLines.reduce((acc, line) => ({
                            ...acc,
                            [line]: parsedData[line] || { modules: [...defaultModules] }
                        }), {})
                    });
                }
            } catch (error) {
                console.error('Error parsing metadata or saved data:', error);
            }
        }
    }, []);

    // Prefill modules from inspectionData on mount
    useEffect(() => {
        const inspectionData = sessionStorage.getItem('inspectionData');
        if (inspectionData && lines.length > 0) {
            try {
                const parsedInspectionData = JSON.parse(inspectionData);
                const calibrationData = parsedInspectionData?.FlashTestWitnessRecordAfterCalibration || {};

                const prefilledModules = (line) =>
                    Array(5).fill(null).map((_, index) => {
                        const moduleData = calibrationData[line]?.modules?.[index] || {};
                        return {
                            moduleId: moduleData.moduleId || '',
                            voc: moduleData.voc || '',
                            isc: moduleData.isc || '',
                            vmp: moduleData.vmp || '',
                            imp: moduleData.imp || '',
                            pmax: moduleData.pmax || '',
                            ff: moduleData.ff || '',
                            moduleEfficiency: moduleData.moduleEfficiency || '',
                            ratedCurrent: moduleData.ratedCurrent || '',
                            testDate: moduleData.testDate || '',
                            testTime: moduleData.testTime || '',
                        };
                    });

                setFormData((prevData) => ({
                    ...prevData,
                    ...lines.reduce((acc, line) => ({
                        ...acc,
                        [line]: { modules: prefilledModules(line) }
                    }), {})
                }));
            } catch (error) {
                console.error('Error parsing inspectionData:', error);
            }
        }
    }, [lines]);

    // Persist form data in sessionStorage
    useEffect(() => {
        if (Object.keys(formData).length > 0) {
            sessionStorage.setItem('FlashTestWitnessF10formData', JSON.stringify(formData));
        }
    }, [formData]);

    // Handle input changes for common fields
    const handleCommonChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    // Handle input changes for module fields with autofill
    const handleModuleChange = (line, index, e) => {
        const { name, value } = e.target;
        setFormData((prevData) => {
            const updatedModules = [...prevData[line].modules];
            const currentModule = { ...updatedModules[index], [name]: value };

            if (name === 'moduleId' && value.trim()) {
                const now = new Date();
                currentModule.testDate = now.toISOString().split('T')[0];
                currentModule.testTime = now.toTimeString().slice(0, 5);
            }

            updatedModules[index] = currentModule;
            return { ...prevData, [line]: { modules: updatedModules } };
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsLoading(true);

        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: sessionStorage.getItem('inspectionDate'),
                inspectionShift: sessionStorage.getItem('inspectionShift'),
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                FlashTestWitnessRecordAfterCalibration: {
                    date: formData.date,
                    shift: formData.shift,
                    ...lines.reduce((acc, line) => ({
                        ...acc,
                        [line]: formData[line]
                    }), {})
                },
            };

            await submitFormData(payload);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <h2 className="text-2xl font-bold text-center mb-4">Flash Test Witness Record After Calibration</h2>
            <p className="text-center font-semibold mb-4">Frequency: 5 Modules Per Shift</p>

            {/* Common Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {commonFields.map(({ label, name, readOnly, type }) => (
                    <div key={name}>
                        <label className="block font-semibold mb-1">{label}</label>
                        {type === 'select' ? (
                            <select
                                name={name}
                                value={formData[name] || ''}
                                onChange={handleCommonChange}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                disabled={lines.length === 0}
                            >
                                {lines.length === 0 ? (
                                    <option value="">No lines available</option>
                                ) : (
                                    lines.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))
                                )}
                            </select>
                        ) : (
                            <input
                                type={name === 'date' ? 'date' : 'text'}
                                name={name}
                                value={formData[name] || ''}
                                onChange={handleCommonChange}
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                readOnly={readOnly}
                            />
                        )}
                    </div>
                ))}
            </div>

            {/* Module Sections for Selected Line */}
            {lines.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-4">{formData.line}</h3>
                    {formData[formData.line]?.modules.map((module, index) => (
                        <div key={index} className="border-t pt-4">
                            <h4 className="text-lg font-semibold mb-2">Module {index + 1}</h4>
                            <div className="grid grid-cols-1 mb-4 md:grid-cols-3 gap-4">
                                {moduleFields.map(({ label, name, type }) => (
                                    <div key={name}>
                                        <label className="block font-semibold mb-1">{label}</label>
                                        <input
                                            type={type || 'text'}
                                            name={name}
                                            value={module[name] || ''}
                                            onChange={(e) => handleModuleChange(formData.line, index, e)}
                                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                                            step={name === 'testTime' ? '1' : undefined}
                                            max={name === 'testDate' ? getCurrentDate() : undefined}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form9')}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-[#424752] text-white rounded hover:bg-gray-500 disabled:bg-gray-400"
                >
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form11')}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_10;